import BaseModel from './BaseModel';
import * as AT from '../../components/container/attributes';
import {translate} from '@hit/base/src/plugins/i18n/i18n';

// CONSTANT IDS FOR THE DIFFERENT COMMUNICATION MODES
const ID_PHONE = 'e74723ed-d24c-4ab1-8edc-a5f012dbe8ca';
const ID_FAX = 'c163ebfd-9108-4a74-aa39-4554efbd410a';
const ID_MOBILE_PHONE = '2291c24e-52de-40be-bbfc-c1dd9181717e';
const ID_EMAIL = 'c4d2a1b7-2bd3-4d3b-aea1-9136d25bfda3';

export default class CommunicationMode extends BaseModel {
  /**
   * This class is also used as base class for the different communication mode models (address_communication_mode_data, etc.)
   */
  constructor({
    id,
    value = undefined,
    comment = undefined,
    mode = undefined,
    sortOrder = undefined,
  }) {
    super(id, value, comment);
    this.mode = mode;
    this.sortOrder = sortOrder;
  }

  static get apiRoute() {
    return 'communication_mode';
  }

  static get joinAttrsKey() {
    return ['value', 'comment', 'mode', 'sortOrder'];
  }

  static get allAttributes() {
    return [
      new AT.StringAttribute({
        name: 'value',
        tableProperties: {
          header: 'hit-base.common.value',
          maxWidth: '0.5fr',
        },
      }),
      new AT.StringAttribute({
        name: 'comment',
        tableProperties: {
          header: 'hit-base.common.comment',
          maxWidth: '1fr',
        },
      }),
      new AT.StringAttribute({
        name: 'mode',
        column: 'communication_mode_id',
      }),
      new AT.NumberAttribute({
        name: 'sortOrder',
        column: 'sort_order',
        readOnly: true,
      }),
    ];
  }

  /**
   * These static methods are used to handle the different communication types and how they are displayed
   */
  static get ID_PHONE() {
    return ID_PHONE;
  }
  static get ID_FAX() {
    return ID_FAX;
  }
  static get ID_MOBILE_PHONE() {
    return ID_MOBILE_PHONE;
  }
  static get ID_EMAIL() {
    return ID_EMAIL;
  }

  // All possible communication mode options
  static get allOptions() {
    return [
      {
        id: CommunicationMode.ID_PHONE,
        key: 'hit-app.common.phone',
        designation: translate('hit-app.common.phone'),
        icon: 'phone',
      },
      {
        id: CommunicationMode.ID_MOBILE_PHONE,
        key: 'hit-app.common.mobile',
        designation: translate('hit-app.common.mobile'),
        icon: 'mobile-phone',
      },
      {
        id: CommunicationMode.ID_FAX,
        key: 'hit-app.common.fax',
        designation: translate('hit-app.common.fax'),
        icon: 'printer',
      },
      {
        id: CommunicationMode.ID_EMAIL,
        key: 'hit-app.common.mail',
        designation: translate('hit-app.common.mail'),
        icon: 'email',
      },
    ];
  }

  // Based on the id, it returns the communication mode option object with all the necessary information
  static getOption(comModeId) {
    if (!comModeId) return undefined;
    return this.allOptions.find((option) => option.id === comModeId);
  }

  // Used in the fast mode browser to display prefix icon for selected communication mode
  static getPrefixIcon(option) {
    return option?.icon;
  }

  // Returns the href to redirect to when the user clicks on the value of the communication mode
  static getHref(communicationModeId, value) {
    switch (communicationModeId) {
      case CommunicationMode.ID_PHONE:
        return `tel:${value}`;
      case CommunicationMode.ID_MOBILE_PHONE:
        return `tel:${value}`;
      case CommunicationMode.ID_FAX:
        return `tel:${value}`;
      case CommunicationMode.ID_EMAIL:
        return `mailto:${value}`;
      default:
        return undefined;
    }
  }

  static factory() {
    return new CommunicationMode();
  }
}
